import { BaseClient } from '../base-client';
import type {
  CreateReportLabelInput,
  ReportLabel,
  ReportLabelItem,
  ReportLabelsResponse,
  ReportLabelTarget,
} from './types';

/**
 * Typed client for the Reports page's labels (`/api/report-labels/*`).
 *
 * A label ORGANISES the list; it never grants access. Filing a report under a
 * label shows it there only to people who could already open it, so nothing
 * here is a way of sharing — that is `ReportDefinitionsClient.replaceShares`.
 *
 * `report.view` is enough for personal labels and stars. Anything STANDARD —
 * making, renaming, deleting one, or filing under it — needs `report.label`;
 * `ReportLabel.editable` says which applies to each label.
 */
export class ReportLabelsClient extends BaseClient {
  /** Every label the caller sees, and every report under them they may open. */
  list(): Promise<{ data: ReportLabelsResponse }> {
    return this.request('/api/report-labels');
  }

  create(input: CreateReportLabelInput): Promise<{ data: ReportLabel }> {
    return this.request('/api/report-labels', { method: 'POST', body: input });
  }

  rename(id: string, name: string): Promise<{ data: ReportLabel }> {
    return this.request(`/api/report-labels/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: { name },
    });
  }

  /** Deletes the label only — the reports under it are untouched. */
  remove(id: string): Promise<{ data: ReportLabel }> {
    return this.request(`/api/report-labels/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  /**
   * File a report under a label. IDEMPOTENT. Pass `'starred'` as the label id
   * to star it — the caller's Starred label is made on first use.
   */
  addItem(
    labelId: string | 'starred',
    target: ReportLabelTarget,
  ): Promise<{ data: ReportLabelItem }> {
    return this.request(`/api/report-labels/${encodeURIComponent(labelId)}/items`, {
      method: 'PUT',
      query: { ...target },
    });
  }

  /** Take a report out of a label. Idempotent, like {@link addItem}. */
  removeItem(
    labelId: string | 'starred',
    target: ReportLabelTarget,
  ): Promise<{ data: ReportLabelItem }> {
    return this.request(`/api/report-labels/${encodeURIComponent(labelId)}/items`, {
      method: 'DELETE',
      query: { ...target },
    });
  }
}
